import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiInfo, FiImage, FiDownload, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';
import ProcessingLoader from '../components/ProcessingLoader';
import './ToolPage.css';

const AIGeneration = () => {
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [style, setStyle] = useState('realistic');
  const [size, setSize] = useState('1024x1024');

  const styles = [
    { value: 'realistic', label: 'Realistic', emoji: '📷' },
    { value: 'artistic', label: 'Artistic', emoji: '🎨' },
    { value: 'anime', label: 'Anime', emoji: '🎌' },
    { value: 'digital-art', label: 'Digital Art', emoji: '💻' },
    { value: 'fantasy', label: 'Fantasy', emoji: '🐉' },
    { value: 'minimalist', label: 'Minimalist', emoji: '⬜' }
  ];

  const sizes = [
    { value: '512x512', label: '512 × 512' },
    { value: '768x768', label: '768 × 768' },
    { value: '1024x1024', label: '1024 × 1024' },
    { value: '1024x768', label: '1024 × 768' },
    { value: '768x1024', label: '768 × 1024' }
  ];

  const examplePrompts = [
    "A serene Japanese garden with cherry blossoms at sunset",
    "A futuristic city skyline with flying cars and neon lights",
    "A majestic lion wearing a crown, portrait style",
    "An enchanted forest with glowing mushrooms and fireflies"
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate an image');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post('/api/ai-generate', {
        prompt,
        style,
        size
      }, {
        responseType: 'blob'
      });

      const imageUrl = URL.createObjectURL(response.data);
      setGeneratedImage(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `ai-generated-${Date.now()}.png`;
    link.click();
  };

  const handleReset = () => {
    setGeneratedImage(null);
    setPrompt('');
    setError(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Generating image..."
          subMessage="Our AI is creating your unique image"
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-dark">
              <FiCpu />
            </div>
            <h1>AI Image Generation</h1>
            <p>Create stunning images from text descriptions using state-of-the-art AI. 
               Bring your imagination to life.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!generatedImage ? (
            <motion.div 
              className="ai-generator-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="prompt-section">
                <label className="prompt-label">Describe your image</label>
                <textarea
                  className="prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="A beautiful mountain landscape at sunset with snow-capped peaks..."
                  rows={4}
                />
                
                <div className="example-prompts">
                  <span>Try:</span>
                  {examplePrompts.map((example, index) => (
                    <button
                      key={index}
                      className="example-btn"
                      onClick={() => setPrompt(example)}
                    >
                      {example.substring(0, 30)}...
                    </button>
                  ))}
                </div>
              </div>

              <div className="options-section">
                <div className="option-group">
                  <label className="option-label">Style</label>
                  <div className="style-grid">
                    {styles.map((s) => (
                      <button
                        key={s.value}
                        className={`style-option ${style === s.value ? 'selected' : ''}`}
                        onClick={() => setStyle(s.value)}
                      >
                        <span className="style-emoji">{s.emoji}</span>
                        <span className="style-name">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="option-group">
                  <label className="option-label">Size</label>
                  <div className="size-options">
                    {sizes.map((s) => (
                      <button
                        key={s.value}
                        className={`size-option ${size === s.value ? 'selected' : ''}`}
                        onClick={() => setSize(s.value)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <motion.div 
                className="process-actions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <button 
                  className="btn-process btn-generate"
                  onClick={handleGenerate}
                  disabled={isProcessing || !prompt.trim()}
                >
                  <FiImage /> Generate Image
                </button>
              </motion.div>

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              className="generated-result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="generated-image-container">
                <img src={generatedImage} alt="AI Generated" />
              </div>
              <div className="generated-prompt">
                <strong>Prompt:</strong> {prompt}
              </div>
              <div className="result-actions">
                <button className="action-btn primary" onClick={handleDownload}>
                  <FiDownload /> Download Image
                </button>
                <button className="action-btn secondary" onClick={handleReset}>
                  <FiRefreshCw /> Generate Another
                </button>
              </div>
            </motion.div>
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>Tips for better results</h3>
              <p>Be specific and descriptive in your prompts. Include details about style, 
                 colors, lighting, and composition. The more detailed your description, 
                 the better the results will be.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIGeneration;
